// apps/web/src/components/SocialShare.tsx
// 台灣常用社群分享：LINE / Facebook / Threads(脆) + iOS 原生分享列
import { useMemo, useState } from 'react';
import { Share2, Link2, Check, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';

export interface SocialShareProps {
  url: string;
  title: string;
  text?: string;
  /** 緊湊模式（按鈕列）或完整（含「手機分享」大按鈕） */
  variant?: 'bar' | 'full';
  className?: string;
}

function encode(s: string) {
  return encodeURIComponent(s);
}

/** 各平台分享連結 */
export function buildShareLinks(url: string, title: string, text?: string) {
  const body = text ? `${text}\n${url}` : `${title}\n${url}`;
  return {
    line: `https://social-plugins.line.me/lineit/share?url=${encode(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encode(url)}`,
    // Threads（脆）：文字 + 網址
    threads: `https://www.threads.net/intent/post?text=${encode(body)}`,
    // Messenger 網頁備援（App 內較準）
    messenger: `https://www.facebook.com/dialog/send?link=${encode(url)}&redirect_uri=${encode(url)}`,
  };
}

async function copyToClipboard(url: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch {
    /* fallthrough */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = url;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * 優先 Web Share API（iOS Safari 會跳出系統面板：LINE、FB、IG、訊息…）
 * 不支援時 fallback 開各平台連結
 */
export async function nativeShare(opts: {
  url: string;
  title: string;
  text?: string;
}): Promise<'shared' | 'cancelled' | 'unsupported'> {
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({
        title: opts.title,
        text: opts.text || opts.title,
        url: opts.url,
      });
      return 'shared';
    } catch (e) {
      // 使用者取消
      if (e instanceof DOMException && e.name === 'AbortError') return 'cancelled';
      return 'unsupported';
    }
  }
  return 'unsupported';
}

function ShareIconBtn({
  label,
  href,
  onClick,
  className,
  children,
}: {
  label: string;
  href?: string;
  onClick?: () => void;
  className: string;
  children: React.ReactNode;
}) {
  const base =
    'inline-flex items-center justify-center w-10 h-10 rounded-full text-white shadow-sm hover:opacity-90 active:scale-95 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-alumni-600'

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        title={label}
        className={`${base} ${className}`}
      >
        {children}
      </a>
    );
  }
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={`${base} ${className}`}
    >
      {children}
    </button>
  );
}

export default function SocialShare({
  url,
  title,
  text,
  variant = 'full',
  className = '',
}: SocialShareProps) {
  const [copied, setCopied] = useState(false);
  const links = useMemo(() => buildShareLinks(url, title, text), [url, title, text]);
  const canNative =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const handleNative = async () => {
    const r = await nativeShare({ url, title, text });
    if (r === 'unsupported') {
      // 桌面：開更多選項 → 複製連結
      const ok = await copyToClipboard(url);
      if (ok) {
        setCopied(true);
        toast.success('已複製連結，可貼到 IG、脆等 App');
        setTimeout(() => setCopied(false), 2000);
      } else {
        toast.error('無法分享，請手動複製網址');
      }
    }
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      toast.success('連結已複製');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('複製失敗');
    }
  };

  const iconRow = (
    <div className="flex flex-wrap items-center gap-2.5" role="group" aria-label="社群分享">
      {/* LINE */}
      <ShareIconBtn label="分享到 LINE" href={links.line} className="bg-[#06C755]">
        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden>
          <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
        </svg>
      </ShareIconBtn>

      {/* Facebook */}
      <ShareIconBtn label="分享到 Facebook" href={links.facebook} className="bg-[#1877F2]">
        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden>
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      </ShareIconBtn>

      {/* Threads 脆 */}
      <ShareIconBtn label="分享到 Threads（脆）" href={links.threads} className="bg-black dark:bg-zinc-800">
        <span className="text-[11px] font-bold tracking-tight">脆</span>
      </ShareIconBtn>

      {/* 複製連結 */}
      <ShareIconBtn
        label="複製連結"
        onClick={handleCopy}
        className="bg-gray-600 dark:bg-gray-500"
      >
        {copied ? <Check className="w-5 h-5" /> : <Link2 className="w-5 h-5" />}
      </ShareIconBtn>

      {/* 更多／原生分享（iOS 會出現 IG 等） */}
      <ShareIconBtn
        label={canNative ? '更多分享（系統）' : '更多'}
        onClick={handleNative}
        className="bg-alumni-600"
      >
        {canNative ? <Share2 className="w-5 h-5" /> : <MoreHorizontal className="w-5 h-5" />}
      </ShareIconBtn>
    </div>
  );

  if (variant === 'bar') {
    return <div className={className}>{iconRow}</div>;
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">分享這張校友名片</p>
      {iconRow}
      <button
        type="button"
        onClick={handleNative}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#1877F2] text-white font-medium text-sm shadow-sm hover:bg-[#166fe5] active:scale-[0.99] transition-all"
      >
        <Share2 className="w-4 h-4" />
        {canNative ? '手機分享（LINE / FB / IG / 脆…）' : '複製連結並分享'}
      </button>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        iPhone：點「手機分享」會開啟系統選單，可選 LINE、Facebook、Instagram、Threads（脆）等。
      </p>
    </div>
  );
}
